/**
 * assets/wc-upload.js
 * Pre-generation upload drop zone. File validation, drag/drop handlers,
 * presigner POST, S3 PUT, orchestrator POST, error display, "Choose file"
 * UI state, State-B re-entry, and pending-gen recovery.
 *
 * Lifted verbatim from assets/wolden-customize.js:
 *   ~lines 570-620   : isAcceptedFile, showZoneError, clearZoneError, setState
 *   ~lines 6596-6613 : setStatusMessage, mapPresignerErrorToFriendly
 *   ~lines 6620-6730 : requestPresignedUrl (with retry/backoff)
 *   ~lines 6730-6790 : uploadToS3
 *   ~lines 6790-7055 : handleFile (upload pipeline, orchestrator invocation)
 *   ~lines 7055-7200 : initZone (drag/drop, file-input, action delegation)
 *   ~lines 7386-7480 : initUploadZoneState
 *   ~lines 7474-7640 : reopenFromStateB
 *   ~lines 4776-4860 : maybeRecoverPendingGen
 *
 * Public surface (consumed by wc-init.js):
 *   export function initUploadZone(rootEl)
 *   export function initUploadZoneState()
 *   export function reopenFromStateB(zoneRoot)
 *   export function maybeRecoverPendingGen()
 *
 * Cross-cutting calls to functions still in the monolith are bridged via the
 * shared window.WoldenCustomize (WC) global, exactly as in the monolith.
 * Functions from sibling modules are imported below.
 *
 * ZERO behaviour change — this is a mechanical lift.
 */

import { log, trace, track, captureNetworkEvent } from './wc77-analytics.js';
import { getSessionId, isEmailCaptured, startShopifyLogin } from './wc2-session.js';
import {
  getNamespaceKey,
  getNamespaceType,
  read,
  add,
  list,
  tempGenerationId,
} from './wc2-state.js';
import { getModal, setModalState, openModal } from './wc2-modal.js';
import { start as startLoadingTicker, stop as stopLoadingTicker, setRecoveryActive as setLoadingRecoveryActive } from './wc77-loading.js';
import { showResult, updateResultPhase1_5, updateResultPhase2, stashResult } from './wc77-result.js';
import { applyCleanBreakDeferred } from './wc-pdp-swap-77.js';
import * as WCExperiments from './wc77-experiments.js';
import { buildFetchGenRequest, mapFetchGenToOrchResult } from './wc2-recovery-map.js?v=2';
import { inFlightCountLabel } from './wc2-zone-state.js';



// ─── Constants ────────────────────────────────────────────────────────────────
// Lifted verbatim from monolith config block (~lines 125-165).

var PRESIGNER_URL    = 'https://4zspqtx7ctlqedttmmoleyc7ki0xzrby.lambda-url.us-east-2.on.aws/';
var PRESIGNER_TOKEN  = 'oz-POM5c8_-bcjQ-qK4W90oOfzibZ1tykVvfakwun4g';
var ORCHESTRATOR_V1_URL = 'https://pboljz7py4yztpystvet2qcryi0egncw.lambda-url.us-east-2.on.aws/';
var ORCHESTRATOR_V2_URL = 'https://lyzv32bofp5symutf7snoe2rxi0avlzj.lambda-url.us-east-2.on.aws/';
var ORCHESTRATOR_TOKEN  = 'q2BzB8woX0dgbBXsr-0TtQorjWz8lXi81Cc64RYtXvM';

// 2026-07-07 — genTest orchestrator (?wolden_test=1 on the PDP URL). A
// SEPARATE COPY of the v2 orchestrator (wired to its own copy of the image
// Lambda) carrying the metafield-authoritative generation changes:
// per-product custom.ai_prompt, STRICT custom.ai_source_image (no
// featured-image fallback), and the text-art 2:3→4:5 conversion
// (custom.art_has_text). The live functions are untouched — only sessions
// whose PDP URL carries ?wolden_test=1 ever reach the copy. One-shot read
// at module load (same semantics as wc_straighton): the flag must be on
// the product page URL where the upload happens.
var ORCHESTRATOR_GENTEST_URL = 'https://m4anmez37am3slq4t476gin5t40owysm.lambda-url.us-east-2.on.aws/';
var WC_GENTEST = (function () {
  try { return new URLSearchParams(location.search).get('wolden_test') === '1'; } catch (e) { return false; }
})();

// ─── In-flight generation registry (2026-06-10 deadline-recovery) ─────────────
// The loading ticker's soft deadline ('wolden:gen-deadline') needs the current
// generationId + zone OUTSIDE the handleFile closure, so the active gen is
// registered here for the lifetime of the orchestrator round-trip.
var _activeGen = null;          // { id, zone } while a generation is in flight
var _recoveryInFlight = false;  // one fetch-gen poll at a time owns the outcome

function _chooseOrchestratorUrl() {
  // 2026-07-07 — genTest wins over every other selector; QA-only by design.
  if (WC_GENTEST) return ORCHESTRATOR_GENTEST_URL;
  try {
    var params = new URLSearchParams(window.location.search);
    var paramVal = params.get('wc_v2');
    if (paramVal === '1') return ORCHESTRATOR_V2_URL;
    if (paramVal === '0') return ORCHESTRATOR_V1_URL;
  } catch (e) { /* ignore */ }
  if (window.WC_SETTINGS && window.WC_SETTINGS.enable_orchestrator_v2 === true) {
    return ORCHESTRATOR_V2_URL;
  }
  return ORCHESTRATOR_V1_URL;
}
var ORCHESTRATOR_URL = _chooseOrchestratorUrl();

// 2026-05-21 — Canary token for straight-on mockup feature. Empty for all real traffic.
var WC_STRAIGHTON_TOKEN = (function () {
  try { return new URLSearchParams(location.search).get('wc_straighton') || ''; } catch (e) { return ''; }
})();

var MAX_BYTES     = 10 * 1024 * 1024;
var ACCEPTED_MIME = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
var ACCEPTED_EXT  = /\.(jpe?g|png|webp|heic|heif)$/i;

// ─── WC global shorthand ──────────────────────────────────────────────────────
// All modules in Sub-project B share the window.WoldenCustomize singleton.
// This alias is intentionally a function (not a top-level const) so it can be
// read lazily (WoldenCustomize may not be initialised when the module first
// loads on browsers that parse modules asynchronously).
function _wc() {
  // 2026-05-19 — V2 namespace fix. Monolith ran as a single IIFE under
  // `window.WoldenCustomize`. V2 split into modules; theme.liquid initialises
  // shared state on `window.WC` (uploads / currentResult / lastResult /
  // generations). Returning the monolith fallback (`window.WoldenCustomize ||
  // {}`) made every write here disappear into a throwaway object: _uploads
  // stayed empty, R.productCtx never got set in showResult, persistCurrentGen
  // bailed for lack of context, localStorage gen-store never populated, and
  // the post-gen drop-zone State-B + reopen-pill paths silently no-op'd.
  // Return window.WC so this module shares state with the rest of V2.
  return window.WC || (window.WC = {});
}

// ─── Validation helpers ────────────────────────────────────────────────────────
// Monolith lines ~570-620.

function isAcceptedFile(file) {
  if (!file) return false;
  if (file.size > MAX_BYTES) return { ok: false, reason: 'too-large' };
  var typeOk = ACCEPTED_MIME.indexOf(file.type) !== -1;
  // Some browsers (esp. for HEIC) report empty file.type — fall back to extension check.
  var extOk  = ACCEPTED_EXT.test(file.name || '');
  if (!typeOk && !extOk) return { ok: false, reason: 'wrong-type' };
  return { ok: true };
}

function showZoneError(zoneRoot, msg) {
  var el = zoneRoot.querySelector('[data-wc-element="error"]');
  if (!el) return;
  el.textContent = msg || '';
  el.hidden = !msg;
  el.style.color = '#c0392b';
}

function clearZoneError(zoneRoot) {
  showZoneError(zoneRoot, '');
}

function setState(uploadZone, state) {
  if (state) {
    uploadZone.setAttribute('data-wc-state', state);
  } else {
    uploadZone.removeAttribute('data-wc-state');
  }
}

// ─── Status message ────────────────────────────────────────────────────────────
// Monolith line ~6596.

function setStatusMessage(zoneRoot, msg, color) {
  var el = zoneRoot.querySelector('[data-wc-element="error"]');
  if (!el) return;
  el.textContent = msg || '';
  el.hidden = !msg;
  el.style.color = color || '#c0392b';
}

// ─── Presigner error mapping ───────────────────────────────────────────────────
// Monolith lines ~6605-6620. Maps presigner HTTP status to customer-facing copy.
// The returned Error has wcUserFacing = true so the outer upload-error catch
// uses the message verbatim instead of wrapping it in a generic fallback.

function mapPresignerErrorToFriendly(status, body) {
  if (status === 429 || status === 503) {
    return "We're seeing a burst of upload traffic — please try again in a moment.";
  }
  if (status === 413) {
    return "That file is too large. Try a smaller photo (under 10MB).";
  }
  if (status === 400 && body && /contentType|content[_-]?type/i.test(String(body.error || ''))) {
    return "We support JPG, PNG, WebP, and HEIC photos. Try a different file.";
  }
  if (status >= 400 && status < 500) {
    return "Couldn't start your upload. Refresh the page and try again.";
  }
  return "Couldn't reach our servers right now. Please try again in a moment.";
}

// ─── Modal error helper ────────────────────────────────────────────────────────
// Monolith line ~6585.

function showModalError(modalErrorTitle, modalErrorMessage) {
  var modal = getModal();
  if (!modal) return;
  setModalState('error');
  var titleEl = modal.querySelector('[data-wc-element="error-title"]');
  var msgEl   = modal.querySelector('[data-wc-element="error-message"]');
  if (titleEl && modalErrorTitle)   titleEl.textContent = modalErrorTitle;
  if (msgEl   && modalErrorMessage) msgEl.textContent   = modalErrorMessage;
  // setModalState synced the dialog's accessible name from the template's
  // default heading BEFORE the custom title landed — re-sync to the final copy.
  try { if (modalErrorTitle) modal.setAttribute('aria-label', modalErrorTitle); } catch (e) {}
}

// ─── Presigner POST with retry/backoff ────────────────────────────────────────
// Monolith lines ~6622-6715.

function requestPresignedUrl(generationId, file, attempt) {
  attempt = attempt || 1;
  var BACKOFF_MS   = [0, 1500, 4500]; // delay BEFORE attempt N (index = attempt - 1)
  var MAX_ATTEMPTS = 3;
  var startedAt    = Date.now();
  return fetch(PRESIGNER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token:       PRESIGNER_TOKEN,
      filename:    file.name,
      contentType: file.type || 'image/jpeg',
      generationId: generationId,
      fileSize:    file.size,
    }),
  }).then(function (resp) {
    var status    = resp.status;
    var retryable = status === 429 || status === 502 || status === 503 || status === 504;
    if (!resp.ok) {
      return resp.json().catch(function () { return {}; }).then(function (errBody) {
        if (retryable && attempt < MAX_ATTEMPTS) {
          // Honor Retry-After (seconds, max 30s sane cap).
          var ra       = resp.headers && resp.headers.get('Retry-After');
          var explicit = null;
          if (ra) {
            var n = parseInt(ra, 10);
            if (n && n > 0 && n < 30) explicit = n * 1000;
          }
          var delay = explicit != null ? explicit : BACKOFF_MS[attempt];
          log('presigner attempt ' + attempt + '/' + MAX_ATTEMPTS + ' HTTP ' + status + ' — retry in ' + delay + 'ms');
          captureNetworkEvent('presigner', PRESIGNER_URL, resp, errBody, null, startedAt, {
            attempt: attempt, will_retry: true, backoff_ms: delay,
          });
          return new Promise(function (resolve, reject) {
            setTimeout(function () {
              requestPresignedUrl(generationId, file, attempt + 1).then(resolve, reject);
            }, delay);
          });
        }
        // Not retryable OR retries exhausted — surface friendly error.
        var friendly = mapPresignerErrorToFriendly(status, errBody);
        var err = new Error(friendly);
        err.wcUserFacing = true;
        err.statusCode   = status;
        err.attempt      = attempt;
        captureNetworkEvent('presigner', PRESIGNER_URL, resp, errBody, err, startedAt, {
          attempt: attempt, will_retry: false, final: true,
        });
        throw err;
      });
    }
    return resp.json().then(function (body) {
      captureNetworkEvent('presigner', PRESIGNER_URL, resp, body, null, startedAt, { attempt: attempt });
      return body;
    });
  }, function (err) {
    // Network-level rejection (fetch threw — DNS, CORS preflight, abort, etc.).
    if (attempt < MAX_ATTEMPTS) {
      var delay = BACKOFF_MS[attempt];
      log('presigner attempt ' + attempt + '/' + MAX_ATTEMPTS + ' network error — retry in ' + delay + 'ms', err && err.message);
      captureNetworkEvent('presigner', PRESIGNER_URL, null, null, err, startedAt, {
        attempt: attempt, will_retry: true, backoff_ms: delay,
      });
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          requestPresignedUrl(generationId, file, attempt + 1).then(resolve, reject);
        }, delay);
      });
    }
    var wrapped = new Error("Upload was interrupted. Check your connection and try again.");
    wrapped.wcUserFacing = true;
    wrapped.attempt      = attempt;
    captureNetworkEvent('presigner', PRESIGNER_URL, null, null, err, startedAt, {
      attempt: attempt, will_retry: false, final: true,
    });
    throw wrapped;
  });
}

// ─── S3 PUT ────────────────────────────────────────────────────────────────────
// Monolith lines ~6715-6790.

function uploadToS3(uploadUrl, file) {
  var contentType = file.type || 'image/jpeg';
  var startedAt   = Date.now();
  log('S3 PUT starting', {
    url:         uploadUrl.split('?')[0],
    contentType: contentType,
    fileBytes:   file.size,
  });
  return fetch(uploadUrl, {
    method:  'PUT',
    headers: { 'Content-Type': contentType },
    body:    file,
  }).then(function (resp) {
    log('S3 PUT response', { status: resp.status, ok: resp.ok });
    if (!resp.ok) {
      return resp.text().then(function (text) {
        var err = new Error('S3 PUT failed: HTTP ' + resp.status + (text ? ' — ' + text.slice(0, 200) : ''));
        captureNetworkEvent('s3-put', uploadUrl.split('?')[0], resp, null, err, startedAt, {
          file_bytes:       file.size,
          body_first_200:   String(text || '').slice(0, 200),
        });
        throw err;
      });
    }
    captureNetworkEvent('s3-put', uploadUrl.split('?')[0], resp, null, null, startedAt, {
      file_bytes: file.size,
    });
    return resp;
  }).catch(function (err) {
    // Distinguish network/CORS errors from HTTP-status errors for diagnosis.
    if (err && err.name === 'TypeError') {
      log('S3 PUT TypeError (network/CORS)', err.message, '- likely CORS preflight blocked or network failure');
      captureNetworkEvent('s3-put', uploadUrl.split('?')[0], null, null, err, startedAt, {
        file_bytes:       file.size,
        cors_or_network:  true,
      });
      var hint = err.message;
      if (/Failed to fetch|NetworkError|fetch/i.test(hint)) {
        hint = 'Browser blocked the upload (CORS or network). Check DevTools → Network tab → preflight OPTIONS request to S3.';
      }
      throw new Error(hint);
    }
    throw err;
  });
}

// ─── handleFile — full upload pipeline ────────────────────────────────────────
// Monolith lines ~6790-7055.

function handleFile(zoneRoot, file) {
  clearZoneError(zoneRoot);

  var validation = isAcceptedFile(file);
  if (!validation.ok) {
    if (validation.reason === 'too-large') {
      showZoneError(zoneRoot, 'That image is over 10MB. Please choose a smaller file.');
    } else if (validation.reason === 'wrong-type') {
      showZoneError(zoneRoot, 'We accept JPG, PNG, WebP, and HEIC. Please choose one of those.');
    }
    return;
  }

  var productCtx = {
    productId:        zoneRoot.getAttribute('data-wc-product-id'),
    productHandle:    zoneRoot.getAttribute('data-wc-product-handle'),
    productType:      zoneRoot.getAttribute('data-wc-product-type'),
    originalImageUrl: zoneRoot.getAttribute('data-wc-original-image-url'),
    prompt:           zoneRoot.getAttribute('data-wc-prompt'),
    artId:            zoneRoot.getAttribute('data-wc-product-art-id') || null,
  };

  if (!productCtx.prompt || productCtx.prompt.length < 10) {
    showZoneError(zoneRoot, 'This product is missing its AI prompt. Please contact support.');
    log('missing prompt on product', productCtx);
    return;
  }
  if (!productCtx.originalImageUrl) {
    showZoneError(zoneRoot, 'This product is missing its original image. Please contact support.');
    log('missing original image on product', productCtx);
    return;
  }

  var generationId = tempGenerationId();
  var startedAt    = Date.now();

  // 2026-05-15 R17 — Persist pending-gen marker so we can recover if
  // Chrome discards the tab during the long Lambda wait. Background tabs
  // (especially incognito) get aggressive tab discarding; sessionStorage
  // survives within the same browsing session.
  try {
    sessionStorage.setItem('wc_pending_gen', JSON.stringify({
      generationId:  generationId,
      startedAt:     Date.now(),
      productHandle: productCtx.productHandle,
      productType:   productCtx.productType,
    }));
  } catch (e) { /* sessionStorage may be unavailable; non-fatal */ }

  log('file selected', {
    name:         file.name,
    type:         file.type,
    sizeMB:       (file.size / (1024 * 1024)).toFixed(2),
    generationId: generationId,
    product:      productCtx,
  });

  track('upload_started', {
    generation_id:  generationId,
    product_id:     productCtx.productId,
    product_handle: productCtx.productHandle,
    file_size_mb:   +(file.size / (1024 * 1024)).toFixed(2),
    file_type:      file.type || 'unknown',
  });

  setStatusMessage(zoneRoot, 'Uploading ' + file.name + '…', '#1d4e8a');

  // Spinner state on the upload-icon circle while the photo uploads.
  var uploadZoneEl = zoneRoot.querySelector('.wolden-customize__upload-zone');
  if (uploadZoneEl) setState(uploadZoneEl, 'uploading');

  requestPresignedUrl(generationId, file)
    .then(function (presignerResp) {
      log('presigner ok', presignerResp);
      return uploadToS3(presignerResp.uploadUrl, file).then(function () {
        return presignerResp;
      });
    })
    .then(function (presignerResp) {
      var elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
      log('S3 upload ok', {
        publicUrl:  presignerResp.publicUrl,
        key:        presignerResp.key,
        elapsedSec: elapsed,
      });

      track('upload_succeeded', {
        generation_id:  generationId,
        product_handle: productCtx.productHandle,
        elapsed_sec:    +elapsed,
        public_url:     presignerResp.publicUrl,
      });

      var wc = _wc();
      if (Array.isArray(wc._uploads)) {
        wc._uploads.push({
          generationId:  generationId,
          file:          file,
          publicUrl:     presignerResp.publicUrl,
          key:           presignerResp.key,
          product:       productCtx,
          uploadedAtMs:  Date.now(),
          elapsedSec:    elapsed,
        });
      }

      // Phase 3: open modal in 'generating' state, kick off orchestrator call.
      var sessionId = getSessionId();
      // 2026-07-01 (Flow B) — payload construction extracted to the exported
      // buildOrchestratorPayload() so the new-flow modules build the IDENTICAL
      // base payload (single source of truth; no drift between flows). Field
      // values and order are byte-for-byte what was inlined here before.
      var orchestratorPayload = buildOrchestratorPayload(productCtx, generationId, presignerResp.publicUrl, null);

      // Experiment harness: stash the PARENT handle (pre-swap, reliable) so the
      // canary gate keys off it and survives the clean swap, then resolve + stamp.
      try { (window.WC = window.WC || {})._parentHandle = productCtx.productHandle; } catch (e) {}
      var _expVariant = WCExperiments.getActiveVariant();
      orchestratorPayload = WCExperiments.stampOrchestratorPayload(orchestratorPayload, _expVariant);
      WCExperiments.fireAssigned(_expVariant, generationId);

      // 2026-06-17 — QSP-gated quota TEST harness (inert without ?wc_quota_test).
      // 'email'/'daily' simulate the gate locally so the popup + sign-in/resume
      // flow can be proven WITHOUT a real generation. The staged payload is the
      // real upload, so the post-login resume fires a genuine generation.
      var _qtMode = _quotaTestMode();
      if (_qtMode === 'email' || _qtMode === 'daily') {
        log('[quota-test] QSP wc_quota_test=' + _qtMode + ' → simulating gate (no orchestrator call)');
        _handleQuotaGate(
          { error: (_qtMode === 'email' ? 'EMAIL_GATE' : 'DAILY_LIMIT'), count: (_qtMode === 'email' ? 3 : 12) },
          orchestratorPayload
        );
        return;
      }

      log('opening modal + calling orchestrator', orchestratorPayload);
      setStatusMessage(zoneRoot, '', null);
      clearZoneError(zoneRoot);
      if (uploadZoneEl) setState(uploadZoneEl, '');

      track('generation_started', {
        generation_id:  generationId,
        product_handle: productCtx.productHandle,
        has_customer_id: !!_getShopifyCustomerId(),
        email_captured:  isEmailCaptured(),
      });

      openModal('generating');
      startLoadingTicker();
      // Post-upload state: a gen is now running. Mark it so (a) the dropzone
      // shows its State-B re-entry banner even while loading and (b) reopen
      // actions know to reopen the LIVE loader, not a not-yet-existent result.
      try { (window.WC = window.WC || {})._genInFlight = true; } catch (e) {}
      try { markZoneInFlight(zoneRoot); } catch (e) { log('markZoneInFlight failed', e && e.message); }

      var orchestratorStartedAt = Date.now();
      // Register for deadline-recovery (a new gen supersedes any stale poll state).
      _activeGen = { id: generationId, zone: zoneRoot };
      _recoveryInFlight = false;
      try { setLoadingRecoveryActive(false); } catch (_) {}
      _runFullGeneration(orchestratorPayload,
        function onPhase1(orchResult) {
          var phase1ElapsedSec = ((Date.now() - orchestratorStartedAt) / 1000).toFixed(1);
          log('generation phase 1 complete', {
            generationId:     orchResult.generationId,
            phase1ElapsedSec: phase1ElapsedSec,
            data:             orchResult.data,
          });
          stopLoadingTicker();
          _activeGen = null; // round-trip finished — deadline recovery no longer applies

          // 2026-05-15 R17 — Clear the pending-gen sessionStorage marker.
          // The gen completed in-tab; no recovery needed.
          try { sessionStorage.removeItem('wc_pending_gen'); } catch (e) {}

          track('generation_phase1_succeeded', {
            generation_id:     orchResult.generationId,
            bubble_id:         orchResult.bubbleId,
            phase1_elapsed_sec: +phase1ElapsedSec,
            product_handle:    productCtx.productHandle,
          });

          var wc2 = _wc();
          if (Array.isArray(wc2._generations)) {
            wc2._generations.push({
              generationId:     orchResult.generationId,
              bubbleId:         orchResult.bubbleId,
              data:             orchResult.data,
              mockups:          orchResult.mockups,
              canvas:           orchResult.canvas,
              canvasFramed:     orchResult.canvasFramed,
              paintKit:         orchResult.paintKit,
              status:           orchResult.status,
              phase1ElapsedSec: phase1ElapsedSec,
              completedAt:      Date.now(),
            });
          }

          // Hydrate the result UI with Phase 1 data — paint-kit composite
          // shows the placeholder until Phase 2 lands.
          showResult(orchResult.generationId, orchResult);
          stashResult(orchResult.generationId, orchResult);

          // 2026-05-21 — Straight-on mockup poll (canary only).
          // straightOnPending=true only when WC_STRAIGHTON_TOKEN activated a canary gen.
          // For all real traffic this block is never entered.
          if (orchResult && orchResult.straightOnPending && !window.WC._straightOnPoll) {
            var __soGenId = orchResult.generationId, __soT0 = Date.now();
            window.WC._straightOnPoll = setInterval(function () {
              if (Date.now() - __soT0 > 120000) {
                clearInterval(window.WC._straightOnPoll); window.WC._straightOnPoll = null; return;
              }
              fetch(ORCHESTRATOR_URL, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                // handleFetchGen reads body.generationIdPrefix (full id works as a prefix).
                body:    JSON.stringify({ token: ORCHESTRATOR_TOKEN, action: 'fetch-gen', generationIdPrefix: __soGenId }),
              }).then(function (r) { return r.json(); }).then(function (g) {
                if (g && g.straightOn && g.straightOn.ready && g.straightOn.url) {
                  clearInterval(window.WC._straightOnPoll); window.WC._straightOnPoll = null;
                  if (window.WC.refreshCanvasStraightOn) window.WC.refreshCanvasStraightOn(g.straightOn.url);
                }
              }).catch(function () {});
            }, 5000);
          }

          // 2026-05-17 — DEFERRED PDP SWAP. Earlier wiring called
          // swapPdpForGeneration() inline here at gen-complete, which fired a
          // ~15-second main-thread freeze on click-around due to:
          //   1. Heavy synchronous img.src rotation across every Horizon
          //      gallery selector (~20+ images on a typical PDP).
          //   2. Each img.src write triggered Horizon's native variant-change
          //      cascade (re-fetching product data, re-rendering selectors).
          //   3. Combined with applyCleanBreakDeferred + updatePdpVariantState
          //      firing on every modal selector click, the total work piled
          //      up past Chrome's 15s "Page Unresponsive" threshold.
          // Fix: match the monolith pattern — defer URL/title/gallery/desc
          // swap to modal-close. flushDeferredSwap() in the close-modal
          // handler will call applyCleanBreakNow when the user finishes.
          try {
            applyCleanBreakDeferred('phase1-complete');
          } catch (e) {
            log('applyCleanBreakDeferred(phase1-complete) error', e && e.message);
          }

          document.dispatchEvent(new CustomEvent('wolden:generation-complete', {
            bubbles: true,
            detail: {
              generationId: orchResult.generationId,
              data:         orchResult.data,
              product:      productCtx,
            },
          }));
        },
        function onPhase2(phase2) {
          var phase2ElapsedSec = ((Date.now() - orchestratorStartedAt) / 1000).toFixed(1);
          log('generation phase 2 complete', {
            generationId:     phase2.generationId,
            phase2ElapsedSec: phase2ElapsedSec,
          });
          updateResultPhase2(phase2);
          track('generation_phase2_succeeded', {
            generation_id:     phase2.generationId,
            phase2_elapsed_sec: +phase2ElapsedSec,
          });
        },
        function onError(err, phase) {
          if (phase === 'phase2' || phase === 'phase1.5') {
            // Non-fatal — customer has art + canvas mockups already.
            // Phase 1.5 failure means ATC stays in "Setting up..." state;
            // reconciler will catch up on the 2-min cron tick.
            log('generation ' + phase + ' error (non-fatal)', err && err.message);
            track('generation_' + phase.replace('.', '_') + '_failed', { error: err && err.message });
            return;
          }
          if (err && err.handled) { stopLoadingTicker(); try { window.WC._genInFlight = false; window.WC._minimized = false; } catch (_) {} return; }

          // 2026-06-17 — Quota gate (EMAIL_GATE / DAILY_LIMIT): open the built
          // popup instead of dead-ending in the generic "High demand" error.
          // _localCallOrchestrator flags err.quotaGate + attaches err.body.
          // This is terminal — the gen was intentionally blocked (not a dropped
          // connection), so skip the fetch-gen recovery poll below.
          // ?wc_quota_test=cap deliberately skips the in-modal popup so the test
          // mirrors the LIVE store, which has no popup wiring and shows the
          // generic "High demand" error for a DAILY_LIMIT (429).
          if (err && err.quotaGate && err.body) {
            _activeGen = null;
            _recoveryInFlight = false;
            try { setLoadingRecoveryActive(false); } catch (_) {}
            try { window.WC._genInFlight = false; window.WC._minimized = false; } catch (_) {}
            try { revertZoneInFlight(zoneRoot); } catch (_) {}
            track('generation_quota_gated', { generation_id: generationId, gate: err.body.error });
            // TERMINAL — never fall through to the recovery poll (the gen was
            // intentionally blocked, not a dropped connection). When the built
            // popup is available (and not the cap test) open it; otherwise show
            // a clean "High demand" error (the ?wc_quota_test=cap path + the
            // no-popup live path). Title is set explicitly so it never falls back
            // to the generic "Something went wrong".
            if (_quotaTestMode() !== 'cap' && _handleQuotaGate(err.body, orchestratorPayload)) {
              return;
            }
            stopLoadingTicker();
            showModalError('High demand', (err.body && err.body.message) || "You've hit today's limit. Try again tomorrow!");
            return;
          }

          // Render the fatal error modal — only when recovery is impossible or
          // has genuinely failed.
          function failHard(e) {
            stopLoadingTicker();
            _activeGen = null;
            _recoveryInFlight = false;
            try { setLoadingRecoveryActive(false); } catch (_) {}
            // Terminal failure ends the in-flight window. Clear the signals and
            // revert the dropzone so no stale spinner pill / re-entry banner
            // survives the error (revert keeps State-B only if finished gens exist).
            try { window.WC._genInFlight = false; window.WC._minimized = false; } catch (_) {}
            try { revertZoneInFlight(zoneRoot); } catch (_) {}
            log('generation failed', e && e.message, e && e.phase);
            track('generation_failed', {
              generation_id: generationId,
              phase:   (e && e.phase)   || 'unknown',
              message: (e && e.message) || 'unknown',
            });
            // 2026-05-15 R1/R12 — Lambda returns friendly body.error text;
            // callOrchestrator wraps AbortError with wcUserFacing=true.
            var title = 'Something went wrong';
            var msg   = (e && e.message) || 'Please try again.';
            if (e && (e.category === 'timeout' || e.name === 'WCTimeoutError' || e.name === 'AbortError')) {
              title = 'Hold on';
            } else if (e && e.phase === 'nanobananapro') {
              title = 'Art service issue';
            } else if (e && (e.errorCategory === 'rate_limit' || e.statusCode === 429)) {
              title = 'High demand';
            }
            showModalError(title, msg);
          }

          // 2026-05-23 — Dropped-connection recovery. A ~70s Phase-1 request can
          // drop on mobile (WebKit "Load failed") AFTER the orchestrator already
          // finished the gen server-side — which both shows this error AND skips
          // finalization. Quota/handled gates returned above; for every other
          // fatal Phase-1 error, poll fetch-gen by the known generationId before
          // dead-ending. On success we show the result, on failure we fall
          // through to failHard, so this can only improve things.
          // 2026-06-10 — window extended 9→30 attempts (~3 min: gens regularly
          // run 90-300s, the old ~54s poll gave up before slow gens finished)
          // and the loader now shows "still working" copy while we poll. If the
          // loader-deadline poll already owns this gen, let it decide — its
          // success path hydrates the same result this one would.
          if (_recoveryInFlight) {
            log('[gen-recovery] orchestrator error while deadline poll active — deferring to it', {
              generationId: generationId, error: err && err.message,
            });
            return;
          }
          if (generationId) {
            log('[gen-recovery] phase1 error — polling fetch-gen before failing', {
              generationId: generationId, error: err && err.message,
            });
            _recoveryInFlight = true;
            try { setLoadingRecoveryActive(true); } catch (_) {}
            track('generation_recovery_started', {
              generation_id: generationId,
              trigger:       'orchestrator_error',
              error:         (err && err.message) || 'unknown',
            });
            _recoverGenById(generationId, 30, 6000)
              .then(function (recovered) {
                _recoveryInFlight = false;
                try { setLoadingRecoveryActive(false); } catch (_) {}
                if (recovered) {
                  stopLoadingTicker();
                  _activeGen = null;
                  track('generation_recovered_after_drop', {
                    generation_id:  generationId,
                    original_error: (err && err.message) || 'unknown',
                  });
                  return;
                }
                failHard(err);
              })
              .catch(function () { failHard(err); });
            return;
          }
          failHard(err);
        },
        function onPhase1_5(phase1_5) {
          var p15ElapsedSec = ((Date.now() - orchestratorStartedAt) / 1000).toFixed(1);
          log('generation phase 1.5 complete', {
            generationId:  phase1_5.generationId,
            p15ElapsedSec: p15ElapsedSec,
            finalStatus:   phase1_5.phase,
          });
          updateResultPhase1_5(phase1_5);
          track('generation_phase1_5_succeeded', {
            generation_id:      phase1_5.generationId,
            phase1_5_elapsed_sec: +p15ElapsedSec,
            final_status:       phase1_5.phase,
          });
        }
      );

      // Hook (kept for downstream listeners — fires regardless of modal lifecycle).
      zoneRoot.dispatchEvent(new CustomEvent('wolden:upload-complete', {
        bubbles: true,
        detail: {
          generationId: generationId,
          publicUrl:    presignerResp.publicUrl,
          key:          presignerResp.key,
          file:         file,
          product:      productCtx,
          sessionId:    sessionId,
        },
      }));
    })
    .catch(function (err) {
      log('upload error', err && err.message);
      if (uploadZoneEl) setState(uploadZoneEl, '');
      // 2026-05-15 — Errors flagged wcUserFacing already carry friendly copy.
      // Show verbatim. Only unflagged developer errors get the generic wrapper,
      // and even that no longer leaks status codes.
      var displayMsg;
      if (err && err.wcUserFacing && err.message) {
        displayMsg = err.message;
      } else {
        displayMsg = 'Upload failed. Please try again.';
      }
      showZoneError(zoneRoot, displayMsg);
      track('upload_failed', {
        generation_id:  generationId,
        message:        (err && err.message) || 'unknown',
        wc_user_facing: !!(err && err.wcUserFacing),
      });
    });
}

// ─── Private cross-module helpers ─────────────────────────────────────────────

/**
 * Read Shopify customer.id set by Liquid in theme.liquid head.
 * Mirrors monolith getShopifyCustomerId() at line ~843.
 */
function _getShopifyCustomerId() {
  return (window.WoldenShopifyCustomerId && String(window.WoldenShopifyCustomerId)) || null;
}

/**
 * buildOrchestratorPayload — the ONE place the Phase-1 orchestrator payload is
 * assembled (2026-07-01, Flow B build; previously inlined in handleFile).
 *
 * WHY exported: the new flow modules (wc77-flow-b.js, later wc77-flow-c.js)
 * run their own upload UX but must send the exact same base payload — token,
 * quota-gate toggles, canary token, session identity — or the flows would
 * silently drift apart server-side. Flow-specific additions (petName, email,
 * emailCaptured:true, skipProducts) ride in `extras`, merged LAST so a flow
 * can also override a base field deliberately (e.g. emailCaptured).
 *
 * @param {object} productCtx  — {productId, productHandle, originalImageUrl, prompt, ...}
 * @param {string} generationId
 * @param {string} petImageUrl — presigner publicUrl of the uploaded photo
 * @param {object|null} extras — additive flow-specific fields, merged last
 */
export function buildOrchestratorPayload(productCtx, generationId, petImageUrl, extras) {
  var payload = {
    token:             ORCHESTRATOR_TOKEN,
    generationId:      generationId,
    petImageUrl:       petImageUrl,
    originalImageUrl:  productCtx.originalImageUrl,
    prompt:            productCtx.prompt,
    productId:         productCtx.productId,
    productHandle:     productCtx.productHandle,
    woldenSessionId:   getSessionId(),
    shopifyCustomerId: _getShopifyCustomerId(),
    emailCaptured:     isEmailCaptured(),
    // 2026-05-17 — Operator toggle from theme settings. When ON, the
    // Lambda skips EMAIL_GATE + DAILY_LIMIT for testing. Default false.
    // 2026-06-17 — ?wc_quota_test=enforce forces real server enforcement for
    // THIS session only; everyone else keeps the theme-setting bypass.
    disableQuotaGates: _quotaTestMode() === 'enforce'
      ? false
      : (window.WC_SETTINGS && window.WC_SETTINGS.disable_quota_gates === true),
    // 2026-06-18 — ?wc_quota_test=cap trips the server's 12/day cookie cap on
    // the FIRST generation, so the 130s decoy + "High demand" reject can be
    // tested without doing 12 real gens. Only ever blocks this caller.
    forceCookieCap: _quotaTestMode() === 'cap',
    // 2026-05-21 — straight-on canary token; empty string for all real traffic.
    straightOnCanaryToken: WC_STRAIGHTON_TOKEN,
    // 2026-07-07 — ?wolden_test=1: the genTest orchestrator copy resolves the
    // prompt/source-art/has-text from the product's metafields server-side.
    // undefined for all real traffic → JSON.stringify drops the key entirely,
    // so the normal-path payload stays byte-identical.
    genTest: WC_GENTEST || undefined,
    // 2026-07-02 — arm-of-record fields (tracking-layer-spec §1.3 layer 4).
    // The generation payload carries the whole-flow assignment so the Bubble
    // record becomes the join spine + cross-device tiebreak (same pattern as
    // experimentVariant/phDistinctId, which the modal path stamps via
    // stampOrchestratorPayload). Null-safe: WC_FLOW is written by the head
    // assigner on every page — even its error path — but belt-and-braces
    // anyway. subVariants is null while sub-variants are dormant (pickSub
    // returns null for every arm today). The standalone watercolor page
    // overrides flowArm through `extras` ('watercolor_standalone') because
    // its generations are OUTSIDE the test and must not count as a test arm.
    flowArm:     (window.WC_FLOW && window.WC_FLOW.arm) || null,
    subVariants: (window.WC_FLOW && window.WC_FLOW.subVariant) || null,
    wcUid:       (window.WC_FLOW && window.WC_FLOW.uid) || null,
  };
  if (extras) {
    for (var k in extras) {
      if (Object.prototype.hasOwnProperty.call(extras, k) && extras[k] !== undefined) {
        payload[k] = extras[k];
      }
    }
  }
  return payload;
}

/**
 * runFullGeneration — orchestrates the two-phase + Phase 1.5 orchestrator
 * flow. Bridges via window.WoldenCustomize so the monolith's callOrchestrator
 * (which owns the quota-gate logic, AbortController, telemetry, etc.) is
 * reused unchanged.
 * Monolith lines ~2961-3030.
 */
// Minimal inline callOrchestrator (v2-only path; no monolith required).
// Mirrors monolith's callOrchestrator happy path + basic error handling.
// Full quota-gate UI (EMAIL_GATE, DAILY_LIMIT) is deferred — surfaces as
// generic "QUOTA" error for now.
function _localCallOrchestrator(payload, signal) {
  return fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: signal,
  }).then(function (resp) {
    return resp.text().then(function (text) {
      var body;
      try { body = text ? JSON.parse(text) : {}; }
      catch (e) {
        var pErr = new Error('Orchestrator returned non-JSON (HTTP ' + resp.status + ')');
        pErr.statusCode = resp.status;
        throw pErr;
      }
      if (!resp.ok || !body.success) {
        var isQuotaGate = resp.status === 429 ||
          (body && (body.error === 'EMAIL_GATE' || body.error === 'DAILY_LIMIT'));
        var err = new Error((body && body.error) || ('HTTP ' + resp.status));
        err.statusCode = resp.status;
        err.phase = body && body.phase;
        err.quotaGate = isQuotaGate;
        err.body = body;
        throw err;
      }
      return body;
    });
  });
}

// 2026-06-17 — QSP-gated quota TEST mode. Inert unless the URL carries
// ?wc_quota_test=<value>. Lets us prove the gate/popup on a preview theme
// without affecting real customers:
//   email   → simulate the EMAIL_GATE popup (no orchestrator call, no real gen)
//   daily   → simulate the DAILY_LIMIT popup
//   enforce → real server enforcement for THIS session only (send disableQuotaGates:false)
function _quotaTestMode() {
  try {
    var v = new URL(window.location.href).searchParams.get('wc_quota_test');
    if (v === 'email' || v === 'daily' || v === 'enforce' || v === 'cap') return v;
  } catch (_) {}
  return null;
}

// 2026-06-17 — Quota-gate popup trigger (EMAIL_GATE / DAILY_LIMIT).
// Ported from the monolith's handleQuotaGate (wolden-customize.js ~2817).
// The V2 rewrite detected the quota response (err.quotaGate) but deferred the
// UI to a generic error; this reconnects the already-built popup. The sign-in +
// resume-after-login machinery (startShopifyLogin / maybeResumePostLogin) is
// already imported and booted by wc77-init.js, so this only adds the trigger.
// Google Sign-In is intentionally omitted (its modal markup is commented out
// pending OAuth setup) — email/Shopify-login is the live path.
// Returns true if it handled the error (caller should stop and not fall through).
function _handleQuotaGate(errBody, originalPayload) {
  if (errBody && errBody.error === 'EMAIL_GATE') {
    stopLoadingTicker();
    setModalState('email-gate');
    try { track('email_gate_shown', { count: errBody.count }); } catch (_) {}
    var modal = getModal();
    var btn = modal && modal.querySelector('[data-wc-element="signin-button"]');
    if (btn && !btn.__wcWired) {
      btn.__wcWired = true;
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var noteEl = modal.querySelector('[data-wc-element="email-error"]');
        try {
          btn.disabled = true;
          btn.textContent = 'Redirecting…';
          if (noteEl) {
            noteEl.style.color = '#594427';
            noteEl.textContent = "Taking you to Shopify to verify your email — we'll bring you right back to finish your art.";
          }
        } catch (uiErr) { log('email-gate UI transition error', uiErr && uiErr.message); }
        setTimeout(function () { startShopifyLogin('', originalPayload); }, 700);
      });
    }
    return true;
  }
  if (errBody && errBody.error === 'DAILY_LIMIT') {
    stopLoadingTicker();
    setModalState('daily-limit');
    try { track('daily_limit_shown', { count: errBody.count }); } catch (_) {}
    return true;
  }
  return false;
}

// 2026-06-17 — QSP-gated console test hook. Attached ONLY when the URL carries
// ?wc_quota_test=… , so it's inert for real customers. Lets verification open the
// popup directly (same _handleQuotaGate the live path uses) without an upload:
//   window.WC.__quotaTest('email')  → email-gate popup
//   window.WC.__quotaTest('daily')  → daily-limit popup
try {
  if (_quotaTestMode()) {
    (window.WC = window.WC || {}).__quotaTest = function (kind) {
      return _handleQuotaGate(
        { error: (kind === 'daily' ? 'DAILY_LIMIT' : 'EMAIL_GATE'), count: (kind === 'daily' ? 12 : 3) },
        { token: ORCHESTRATOR_TOKEN }
      );
    };
  }
} catch (e) {}

function _runFullGeneration(payload, onPhase1, onPhase2, onError, onPhase1_5) {
  var wc = _wc();
  // Primary path: monolith exposes runFullGeneration on WC.
  if (typeof wc.runFullGeneration === 'function') {
    return wc.runFullGeneration(payload, onPhase1, onPhase2, onError, onPhase1_5);
  }
  // Use either monolith's callOrchestrator OR the local inline version.
  // _localCallOrchestrator is always available; the monolith version is
  // preferred when present (richer error/timeout/telemetry).
  var callOrch = (typeof wc.callOrchestrator === 'function')
    ? wc.callOrchestrator
    : _localCallOrchestrator;

  return callOrch(payload).then(function (phase1) {
      var isLegacy   = phase1 && phase1.phase === 'phase1_ready';
      var isArtReady = phase1 && phase1.phase === 'art_ready';
      if (!isLegacy && !isArtReady) {
        if (onPhase1) onPhase1(phase1);
        if (onPhase2) onPhase2(phase1);
        return;
      }
      if (onPhase1) {
        try { onPhase1(phase1); } catch (e) { log('onPhase1 handler error', e && e.message); }
      }
      // 2026-05-19 (revision): V2 art_ready returns Shopify state INLINE
      // (canvas.shopify, canvasFramed.shopify, paintKit) so there is no
      // separate Phase 1.5 fetch — onPhase1's showResult already extracts
      // those fields into R. The v2 lambda's `continuation` is for Phase 2
      // only (action === "continue-phase-2"), which generates the PBN
      // breakdown + paint-kit composite (~30-60s downstream).
      //
      // Previously this block fired `continuation` as Phase 1.5 (calling
      // onPhase1_5), AND a second copy as Phase 2 (calling onPhase2) — both
      // hit the v2 lambda, which on a missing continue-phase-2 handler
      // fell into handlePhase1 and 400'd on `Invalid 'petImageUrl'`. With
      // the lambda's handlePhase2 now wired, route the call to onPhase2
      // exactly once. Legacy `phase1_ready` (V1 lambda) shape kept for
      // back-compat — fires phase 2 via phase1.continuation OR phase1
      // itself (legacy original behaviour).
      var p2cont = phase1.phase2Continuation
                || (phase1.continuation && phase1.continuation.action === 'continue-phase-2' ? phase1.continuation : null)
                || (isLegacy ? phase1.continuation : null);
      if (p2cont) {
        callOrch(Object.assign({ token: ORCHESTRATOR_TOKEN, straightOnCanaryToken: WC_STRAIGHTON_TOKEN }, p2cont))
          .then(function (phase2) {
            if (onPhase2) {
              try { onPhase2(phase2); } catch (e) { log('onPhase2 error', e && e.message); }
            }
          }).catch(function (err) {
            if (onError) onError(err, 'phase2');
          });
      } else if (isArtReady && phase1.continuation) {
        // Fallback: continuation present but action unrecognised — treat as
        // legacy Phase 1.5 (some old lambda versions used this shape).
        callOrch(Object.assign({ token: ORCHESTRATOR_TOKEN }, phase1.continuation))
          .then(function (phase1_5) {
            if (onPhase1_5) {
              try { onPhase1_5(phase1_5); } catch (e) { log('onPhase1_5 error', e && e.message); }
            }
          })
          .catch(function (err) {
            if (onError) onError(err, 'phase1.5');
          });
      }
    }).catch(function (err) {
      if (onError) onError(err, 'phase1');
    });
}

// ─── initZone — wire up a single upload-zone root ─────────────────────────────
// Monolith lines ~7055-7200.

function initZone(zoneRoot) {
  if (!zoneRoot || zoneRoot.__wcInitialized) return;
  zoneRoot.__wcInitialized = true;

  var uploadZone = zoneRoot.querySelector('.wolden-customize__upload-zone');
  var fileInput  = zoneRoot.querySelector('[data-wc-element="file-input"]');
  if (!uploadZone || !fileInput) return;

  // Click to open file picker — handled natively by <label for=...>.
  // We only need keyboard handling on the label (Enter / Space).
  uploadZone.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  fileInput.addEventListener('change', function (e) {
    var file = e.target.files && e.target.files[0];
    if (file) handleFile(zoneRoot, file);
  });

  // Drag-and-drop on the zone
  ['dragenter', 'dragover'].forEach(function (evt) {
    uploadZone.addEventListener(evt, function (e) {
      e.preventDefault();
      e.stopPropagation();
      setState(uploadZone, 'dragover');
    });
  });

  ['dragleave', 'drop'].forEach(function (evt) {
    uploadZone.addEventListener(evt, function (e) {
      e.preventDefault();
      e.stopPropagation();
      setState(uploadZone, null);
    });
  });

  uploadZone.addEventListener('drop', function (e) {
    var dt = e.dataTransfer;
    if (!dt || !dt.files || !dt.files.length) return;
    handleFile(zoneRoot, dt.files[0]);
  });

  // Prevent the browser from navigating away if a drop happens outside the zone.
  // Only attach once per page.
  if (!_wc().__windowDragGuardAttached) {
    _wc().__windowDragGuardAttached = true;
    ['dragover', 'drop'].forEach(function (evt) {
      window.addEventListener(evt, function (e) {
        // If the drop target is outside any wolden zone, swallow it.
        var inside = false;
        var t = e.target;
        while (t && t !== document.body) {
          if (
            t.classList &&
            t.classList.contains('wolden-customize__upload-zone')
          ) {
            inside = true;
            break;
          }
          t = t.parentNode;
        }
        if (!inside) {
          e.preventDefault();
        }
      });
    });
  }

  // V1 multi-gen — zone-level click delegation for State B controls
  // (reopen-from-state-b / restart-upload). Lives on the PDP, not in
  // the modal, so the modal's click delegator wouldn't see these.
  zoneRoot.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-wc-action]');
    if (!trigger || !zoneRoot.contains(trigger)) return;
    var action = trigger.getAttribute('data-wc-action');
    if (action === 'reopen-from-state-b') {
      e.preventDefault();
      // If a gen is still running, reopen the LIVE loader — no result exists yet
      // so reopenFromStateB would have nothing to show. Mirrors expand-progress-pill.
      if (window.WC && window.WC._genInFlight) {
        try { setModalState('generating'); } catch (e2) { /* non-fatal */ }
        window.WC._minimized = false;
        var _ppEl = document.querySelector('[data-wc-component="progress-pill"]'); if (_ppEl) _ppEl.hidden = true;
        var _rpEl = document.querySelector('[data-wc-component="reopen-pill"]');  if (_rpEl) _rpEl.hidden = true;
        return;
      }
      reopenFromStateB(zoneRoot);
    } else if (action === 'restart-upload') {
      e.preventDefault();
      var sa    = zoneRoot.querySelector('[data-wc-element="upload-state-a"]');
      var sb    = zoneRoot.querySelector('[data-wc-element="upload-state-b"]');
      if (sa) sa.hidden = false;
      if (sb) sb.hidden = true;
      var input = zoneRoot.querySelector('[data-wc-element="file-input"]');
      if (input) input.click();
    }
  });

  var wc = _wc();
  if (Array.isArray(wc._zones)) wc._zones.push(zoneRoot);
  log('zone initialized', zoneRoot);
}

// ─── Public: initUploadZone ───────────────────────────────────────────────────
// Entry point called by wc-init.js. Wires up a single zone root element.

export function initUploadZone(rootEl) {
  if (!rootEl) return;
  initZone(rootEl);
}

// ─── Public: initUploadZoneState ─────────────────────────────────────────────
// V1 multi-gen — at page load, check each upload zone's PDP for saved gens.
// If any exist, swap the dashed-orange State A for the warm-bg State B
// re-entry banner ("You have N generations of your art").
// Monolith lines ~7386-7480.

// 2026-05-19 — Expose on window.WC so wc-pdp-swap-15 can re-run it after
// applyCleanBreakNow's URL swap without needing a static import (would form
// a circular dep). Critical for State-B activation when a modal close
// pushes the URL from parent → child PDP — without this re-call, the zone
// stays stuck in State A even though we're now on a child PDP URL.
try { (window.WC = window.WC || {}).initUploadZoneState = function () { return initUploadZoneState(); }; } catch (_) {}

export function initUploadZoneState() {
  var zones = document.querySelectorAll('[data-wc-component="upload-zone"]');
  // 2026-05-13 night — detect child PDP from URL. The dropzone should ALWAYS
  // show SOMETHING — state-A (starting / drag-drop) OR state-B (has-art /
  // "view your art"). On a child PDP, the customer's art exists by URL proof,
  // so state-B should always win, even if LocalStorage has no matching gen
  // (friend share, fresh browser, etc.).
  var onChildPdpUrl = /^\/products\/(custom-canvas-print-|custom-canvas-framed-|custom-paint-by-numbers-kit-)/i.test(window.location.pathname);
  zones.forEach(function (zone) {
    var product = {
      productHandle: zone.getAttribute('data-wc-product-handle'),
      productType:   zone.getAttribute('data-wc-product-type'),
      artId:         zone.getAttribute('data-wc-product-art-id') || null,
    };
    var key  = getNamespaceKey(product);
    var gens = key ? list(key) : [];
    // If no stored gens AND we're not on a child PDP, leave state-A as the
    // visible default. (Parent PDP cold visit gets the upload prompt.)
    if (gens.length === 0 && !onChildPdpUrl) return;

    var stateA  = zone.querySelector('[data-wc-element="upload-state-a"]');
    var stateB  = zone.querySelector('[data-wc-element="upload-state-b"]');
    var countEl = zone.querySelector('[data-wc-element="gens-count"]');
    var nounEl  = zone.querySelector('[data-wc-element="gens-count-noun"]');

    if (stateA) stateA.hidden = true;
    if (stateB) stateB.hidden = false;
    // For LocalStorage-backed case: real count. For child-PDP-without-state
    // case: synthesize count = 1 (the customer is on their own art product).
    var displayCount = gens.length > 0 ? gens.length : 1;
    if (countEl) countEl.textContent = displayCount;
    if (nounEl)  nounEl.textContent  = displayCount === 1 ? 'generation' : 'generations';

    // Bubble-bootstrap short-circuit — if bootstrapStateFromBubble already
    // ran (friend-shared child PDP, no LocalStorage), we have
    // R.generationId + all the format-state we need. Persist it to LocalStorage
    // so State B can access it on future page loads.
    var wc  = _wc();
    var R0  = wc._currentResult;
    var bubbleBootstrapped = R0 && R0.generationId &&
      (R0.canvasFramedShopify || R0.canvasShopify || R0.paintKitShopify) &&
      R0.artDisplayUrl;

    if (bubbleBootstrapped && key) {
      var namespaceType = getNamespaceType(product);
      try {
        add(key, namespaceType, product.productHandle, product.productType, {
          id:          R0.generationId,
          bubbleId:    R0.bubbleId || null,
          imageUrl:    R0.artDisplayUrl,
          petPhotoUrl: R0.petImageUrl || null,
          experimentVariant: WCExperiments.getActiveVariant(),
          snapshot:    JSON.parse(JSON.stringify({
            canvasMockups:       R0.canvasMockups,
            paintKitMockup:      R0.paintKitMockup,
            pbnBwUrl:            R0.pbnBwUrl,
            pbnColoredUrl:       R0.pbnColoredUrl,
            canvasShopify:       R0.canvasShopify,
            canvasFramedShopify: R0.canvasFramedShopify,
            paintKitShopify:     R0.paintKitShopify,
            // 2026-07-02 QA round-3 (round-1 #10) — snapshot the framed /
            // straight-on mockups so a rehydrated reveal rebuilds the same
            // slide decks as a fresh generation.
            canvasFramedMockups: R0.canvasFramedMockups,
            straightOnMockupUrl: R0.straightOnMockupUrl,
          })),
        });
        // Refresh the count now that we added the bubble-sourced gen.
        var freshGens  = list(key);
        var freshCount = freshGens.length > 0 ? freshGens.length : 1;
        if (countEl) countEl.textContent = freshCount;
        if (nounEl)  nounEl.textContent  = freshCount === 1 ? 'generation' : 'generations';
      } catch (err) { log('parent-reentry hydration failed', err && err.message); }
    }
  });
}

// ─── markZoneInFlight / revertZoneInFlight ────────────────────────────────────
// Post-upload dropzone state. markZoneInFlight flips the just-uploaded zone to
// State-B the MOMENT a gen starts (before it finishes), so closing the modal
// mid-load reveals the re-entry banner instead of the empty upload prompt.
// revertZoneInFlight runs when a gen FAILS: keep State-B (with the real finished
// count) if the PDP already has finished gens, otherwise restore State-A so the
// customer can retry.
function _zoneProduct(zoneRoot) {
  return {
    productHandle: zoneRoot.getAttribute('data-wc-product-handle'),
    productType:   zoneRoot.getAttribute('data-wc-product-type'),
    artId:         zoneRoot.getAttribute('data-wc-product-art-id') || null,
  };
}

function markZoneInFlight(zoneRoot) {
  if (!zoneRoot) return;
  var key    = getNamespaceKey(_zoneProduct(zoneRoot));
  var stored = key ? list(key) : [];
  var label  = inFlightCountLabel(stored.length);
  var stateA  = zoneRoot.querySelector('[data-wc-element="upload-state-a"]');
  var stateB  = zoneRoot.querySelector('[data-wc-element="upload-state-b"]');
  var countEl = zoneRoot.querySelector('[data-wc-element="gens-count"]');
  var nounEl  = zoneRoot.querySelector('[data-wc-element="gens-count-noun"]');
  if (stateA) stateA.hidden = true;
  if (stateB) stateB.hidden = false;
  if (countEl) countEl.textContent = label.count;
  if (nounEl)  nounEl.textContent  = label.noun;
}

function revertZoneInFlight(zoneRoot) {
  if (!zoneRoot) return;
  var key    = getNamespaceKey(_zoneProduct(zoneRoot));
  var stored = key ? list(key) : [];
  var stateA = zoneRoot.querySelector('[data-wc-element="upload-state-a"]');
  var stateB = zoneRoot.querySelector('[data-wc-element="upload-state-b"]');
  if (stored.length > 0) {
    var countEl = zoneRoot.querySelector('[data-wc-element="gens-count"]');
    var nounEl  = zoneRoot.querySelector('[data-wc-element="gens-count-noun"]');
    if (stateA) stateA.hidden = true;
    if (stateB) stateB.hidden = false;
    if (countEl) countEl.textContent = stored.length;
    if (nounEl)  nounEl.textContent  = stored.length === 1 ? 'generation' : 'generations';
  } else {
    if (stateB) stateB.hidden = true;
    if (stateA) stateA.hidden = false;
  }
}

// ─── Public: reopenFromStateB ─────────────────────────────────────────────────
// Reopen the modal from PDP State B — restore the most recently active gen for
// this PDP, populate WC._currentResult from its snapshot, set state to 'result',
// render the strip.
// Monolith lines ~7474-7640.

export function reopenFromStateB(zoneRoot) {
  if (!zoneRoot) return;
  var product = {
    productHandle: zoneRoot.getAttribute('data-wc-product-handle'),
    productType:   zoneRoot.getAttribute('data-wc-product-type'),
    artId:         zoneRoot.getAttribute('data-wc-product-art-id') || null,
  };

  // 2026-05-13 night — Bubble-bootstrap short-circuit. If state was already
  // hydrated by bootstrapStateFromBubble (friend-shared child PDP, no
  // LocalStorage), we have R.generationId + all the format-state we need.
  // Skip the LocalStorage lookup and open the modal directly using existing
  // R state. Previously this bailed at the missing LocalStorage and the
  // "View Your Art" button did nothing.
  var wc = _wc();
  var R0 = wc._currentResult;
  var bubbleBootstrapped = R0 && R0.generationId &&
    (R0.canvasFramedShopify || R0.canvasShopify || R0.paintKitShopify) &&
    R0.artDisplayUrl;

  if (bubbleBootstrapped) {
    var hadCtx = R0.productCtx;
    R0.productCtx = hadCtx || {
      productHandle: product.productHandle,
      productType:   product.productType,
      artId:         product.artId,
    };
    trace('reopenFromStateB-bubble-shortcircuit', {
      generationId:    R0.generationId,
      hasCanvasFramed: !!R0.canvasFramedShopify,
      hasCanvas:       !!R0.canvasShopify,
      hasPaintKit:     !!R0.paintKitShopify,
    });
    setModalState('result');
    // Repaint the modal UI with the already-hydrated state.
    if (typeof wc.refreshActiveBranches  === 'function') wc.refreshActiveBranches();
    if (typeof wc.refreshSizeFramePills  === 'function') wc.refreshSizeFramePills();
    if (typeof wc.refreshMockupImages    === 'function') wc.refreshMockupImages();
    if (typeof wc.refreshPricingDisplay  === 'function') wc.refreshPricingDisplay();
    if (typeof wc.updatePdpVariantState  === 'function') wc.updatePdpVariantState();
    if (typeof wc.refreshAtcReadyState   === 'function') wc.refreshAtcReadyState();
    // Paint hero into both deck slot-1s so whichever format the customer
    // toggles to, the art is there.
    var modal0 = getModal();
    if (modal0 && R0.artDisplayUrl) {
      ['[data-wc-element="slide-pk-art"]', '[data-wc-element="slide-canvas-art"]', '[data-wc-element="result-image"]'].forEach(function (sel) {
        var el = modal0.querySelector(sel);
        if (el) el.src = R0.artDisplayUrl;
      });
    }
    track('result_reopened_from_state_b', { generation_id: R0.generationId, source: 'bubble-bootstrap' });
    return;
  }

  var key  = getNamespaceKey(product);
  if (!key) return;
  var data = read(key);
  if (!data || data.gens.length === 0) return;

  // Pick the active gen, falling back to the most-recent (last in array).
  var active = null;
  for (var i = 0; i < data.gens.length; i++) {
    if (data.gens[i].id === data.activeGenId) { active = data.gens[i]; break; }
  }
  if (!active) active = data.gens[data.gens.length - 1];

  var R = wc._currentResult;
  if (!R) {
    log('[wc-upload] reopenFromStateB: WC._currentResult is null; aborting');
    return;
  }

  R.generationId  = active.id;
  R.bubbleId      = active.bubbleId || null;
  R.artDisplayUrl = active.imageUrl;
  R.petImageUrl   = active.petPhotoUrl || R.petImageUrl;
  // Use the CURRENT PDP's context, not the gen's historical entry context.
  // Per spec §9.1: Format Selector defaults to match the PDP the customer is
  // currently on, not where the gen originated.
  R.productCtx = {
    productHandle: product.productHandle || data.lastEntryProductHandle,
    productType:   product.productType   || data.lastEntryProductType,
    artId:         product.artId,
  };

  var snap = active.snapshot || {};
  R.canvasMockups       = snap.canvasMockups       || null;
  R.paintKitMockup      = snap.paintKitMockup      || null;
  R.pbnBwUrl            = snap.pbnBwUrl            || null;
  R.pbnColoredUrl       = snap.pbnColoredUrl       || null;
  R.canvasShopify       = snap.canvasShopify       || null;
  // 2026-05-13 night — missing line bug: reopenFromStateB never hydrated
  // canvasFramedShopify. Customer reopening the modal via the "View Your Art"
  // pill had Canvas Print ATC working but Framed Canvas ATC stuck on
  // "Setting up your custom product..." indefinitely. Fixed here verbatim.
  R.canvasFramedShopify = snap.canvasFramedShopify || null;
  R.paintKitShopify     = snap.paintKitShopify     || null;
  // 2026-07-02 QA round-3 (round-1 #10) — hydrate the framed / straight-on
  // mockups too (reset-first, like the variants above). Without these the
  // reopened reveal built SMALLER slide decks than the fresh one: 2 canvas
  // slides instead of 3, and an art-only canvas-framed deck.
  R.canvasFramedMockups = snap.canvasFramedMockups || null;
  R.straightOnMockupUrl = snap.straightOnMockupUrl || null;

  trace('reopenFromStateB-hydrated', {
    generationId:    active.id,
    stateCanvasKeys: R.canvasShopify       ? Object.keys(R.canvasShopify)       : null,
    stateFramedKeys: R.canvasFramedShopify ? Object.keys(R.canvasFramedShopify) : null,
    hasPaintKit:     !!R.paintKitShopify,
  });

  // Default the format selector based on the entry PDP type (spec §9.1).
  // Phase A v1.1 — also fall back to handle if product.type is empty / misset.
  var pt = (R.productCtx.productType   || '').toLowerCase();
  var ph = (R.productCtx.productHandle || '').toLowerCase();
  R.activeFormat = (pt.indexOf('canvas') !== -1 || ph.indexOf('canvas-print') !== -1) ? 'canvas' : 'paint_kit';
  // Phase A v1.1 — DO NOT reset size/frame here; preserve customer's last
  // selection across state-B reopen (matches the orchestrator success path).

  setModalState('result');

  // Populate hero + slider images.
  if (typeof wc.refreshActiveBranches       === 'function') wc.refreshActiveBranches();
  if (typeof wc.refreshSizeFramePills       === 'function') wc.refreshSizeFramePills();
  if (typeof wc.refreshMockupImages         === 'function') wc.refreshMockupImages();
  if (typeof wc.refreshPricingDisplay       === 'function') wc.refreshPricingDisplay();
  if (typeof wc.applyPdpVisibility          === 'function') wc.applyPdpVisibility();
  if (typeof wc.updatePdpVariantState       === 'function') wc.updatePdpVariantState();
  // 2026-05-13 night — sync ATC button state + start backstop polling so any
  // missing variants get filled in automatically.
  if (typeof wc.refreshAtcReadyState        === 'function') wc.refreshAtcReadyState();
  if (typeof wc.startResolveRecoveryPolling === 'function') wc.startResolveRecoveryPolling();
  // 2026-05-14 v7 — Default slot 2 (idx 1) on reopen, matching PDP spec.
  if (typeof wc.setSlideIndex === 'function') wc.setSlideIndex(R.activeFormat, 1);

  // Set hero (slide #1) to active gen's image directly (cross-fade not needed
  // on initial open).
  var modal = getModal();
  if (modal) {
    ['[data-wc-element="slide-pk-art"]', '[data-wc-element="slide-canvas-art"]'].forEach(function (sel) {
      var el = modal.querySelector(sel);
      if (el) el.src = active.imageUrl;
    });
  }

  if (typeof wc.renderGensStrip === 'function') wc.renderGensStrip();
  track('result_reopened_from_state_b', { generation_id: active.id, gens_count: data.gens.length });
}

// ─── Dropped-connection gen recovery (shared: inline + reload paths) ──────────
// 2026-05-23 — A long (~70s) Phase-1 request can drop on mobile (WebKit throws
// "Load failed") even though the orchestrator finished the gen server-side.
// fetch-gen retrieves a completed gen by id; we map it to the orchResult shape
// showResult expects and open the result modal. Used by the inline onError
// recovery (handleFile) and the on-reload recovery (maybeRecoverPendingGen).
function _wcDelay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

function _hydrateRecoveredGen(body, fallbackGenId) {
  // Double-hydrate guard (2026-06-10): if the original orchestrator response
  // landed while a recovery poll was in flight (or two recovery paths raced),
  // the result is already rendered — just make sure the modal shows it.
  try {
    var wcg = _wc();
    if (wcg && wcg._currentResult && fallbackGenId &&
        wcg._currentResult.generationId === fallbackGenId) {
      openModal('result');
      return wcg._currentResult;
    }
  } catch (e) { /* non-fatal — fall through to a normal hydrate */ }
  // Mapping is in the pure, unit-tested wc2-recovery-map.js (the old inline
  // mapping used wrong field names — body.imageUrl/body.canvasShopify — which
  // never matched the real fetch-gen response).
  var orchResult = mapFetchGenToOrchResult(body, fallbackGenId);
  openModal('result');
  showResult(orchResult.generationId, orchResult);
  // Re-layout the gallery once the modal has real dimensions. The result slider
  // measures 0 if it lays out before the page/modal is sized — which happens on
  // a COLD recovery (reload-recovery on a fresh page, or a recovery that fires
  // before window 'load'). Fire deferred resizes AND one after window 'load' so
  // the recovered art renders in every timing: warm (real drop over a visible
  // 'generating' modal) and cold (fresh-page reload recovery). Re-laying-out an
  // already-good gallery is harmless.
  var _settle = function () { try { window.dispatchEvent(new Event('resize')); } catch (e) {} };
  setTimeout(_settle, 200);
  setTimeout(_settle, 900);
  setTimeout(_settle, 2000);
  try {
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { setTimeout(_settle, 200); }, { once: true });
    }
  } catch (e) {}
  return orchResult;
}

// Poll fetch-gen up to `attempts` times, `delayMs` apart, resolving the RAW
// fetch-gen body once a completed gen (WITH art urls) is found.
// 2026-07-01 (Flow B) — factored out of _recoverGenById and exported so the
// new flows can run the SAME dropped-connection recovery without the modal
// coupling (this function never touches the modal; the caller decides how to
// render a recovered gen). Resolves { stopped, body }:
//   { stopped:true,  body:null }  — shouldStop() said another path owns it
//   { stopped:false, body:{...} } — completed gen found (art urls present)
//   { stopped:false, body:null }  — every attempt exhausted / failed
// NEVER rejects — callers branch on the shape.
export function pollFetchGen(generationId, attempts, delayMs, shouldStop) {
  attempts = attempts || 4;
  delayMs  = delayMs  || 4000;
  function tryOnce(n) {
    // A caller-supplied stop probe (e.g. "result already hydrated by the
    // original response") short-circuits between attempts.
    try {
      if (typeof shouldStop === 'function' && shouldStop()) {
        return Promise.resolve({ stopped: true, body: null });
      }
    } catch (e) { /* non-fatal */ }
    return fetch(ORCHESTRATOR_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildFetchGenRequest(generationId, ORCHESTRATOR_TOKEN)),
    }).then(function (resp) {
      return resp.json().then(function (body) {
        if (resp.ok && body && body.success) {
          // 2026-06-10 — success WITHOUT art urls means the gen record exists
          // but the art hasn't landed yet. Hydrating that opened a sellable
          // modal where every img painted an empty src (live-verified on a
          // version_e recovery: hero + mockups all broken). Treat it as
          // not-ready and keep polling instead.
          var _art = body.art || {};
          var _hasArt = !!(_art.url2K_Watermarked || _art.url2K_Clean || _art.url640_Clean);
          if (_hasArt) {
            log('[gen-recovery] recovered gen on attempt ' + n + ' of ' + attempts);
            return { stopped: false, body: body };
          }
          log('[gen-recovery] fetch-gen success but no art urls yet (attempt ' + n + ' of ' + attempts + ') — not ready');
        }
        if (n < attempts) return _wcDelay(delayMs).then(function () { return tryOnce(n + 1); });
        return { stopped: false, body: null };
      });
    }).catch(function (e) {
      if (n < attempts) return _wcDelay(delayMs).then(function () { return tryOnce(n + 1); });
      log('[gen-recovery] all ' + attempts + ' attempts failed: ' + (e && e.message));
      return { stopped: false, body: null };
    });
  }
  return tryOnce(1);
}

// Poll fetch-gen and hydrate the result MODAL on success. Resolves true once a
// completed gen is found + hydrated into the result modal; false if every
// attempt fails. NEVER rejects — callers branch on the boolean. A gen that
// truly never completed simply returns not-success each time → false → caller
// shows its error, so this can only IMPROVE the current dead-end behaviour.
// (Modal-flow wrapper around pollFetchGen — behavior unchanged by the factor.)
function _recoverGenById(generationId, attempts, delayMs) {
  function _alreadyHydrated() {
    // Already hydrated (the original response or another recovery path won the
    // race while we were waiting between attempts) — stop polling, report success.
    try {
      var wcr = _wc();
      return !!(wcr && wcr._currentResult && wcr._currentResult.generationId === generationId);
    } catch (e) { return false; }
  }
  return pollFetchGen(generationId, attempts, delayMs, _alreadyHydrated).then(function (out) {
    if (out.stopped) return true;
    if (!out.body) return false;
    try { _hydrateRecoveredGen(out.body, generationId); }
    catch (e) { log('[gen-recovery] hydrate threw: ' + (e && e.message)); return false; }
    try { sessionStorage.removeItem('wc_pending_gen'); } catch (e) {}
    return true;
  });
}

// ─── Loader-deadline recovery handoff (2026-06-10) ────────────────────────────
// wc77-loading.js no longer flips to the error template at its 150s deadline —
// it emits 'wolden:gen-deadline' and keeps the loader up with patient copy.
// We take ownership here: the orchestrator usually HAS finished server-side
// (verified live: a reload recovered the art that the dead-ended modal hid),
// so poll fetch-gen for the registered in-flight gen for up to ~3.5 more
// minutes. Only when polling exhausts is the hard error honest.
window.addEventListener('wolden:gen-deadline', function () {
  if (!_activeGen || !_activeGen.id || _recoveryInFlight) return;
  var genId = _activeGen.id;
  var zone  = _activeGen.zone;
  _recoveryInFlight = true;
  try { setLoadingRecoveryActive(true); } catch (_) {}
  log('[gen-recovery] loader deadline reached — polling fetch-gen', { generationId: genId });
  track('generation_recovery_started', { generation_id: genId, trigger: 'loader_deadline' });
  _recoverGenById(genId, 35, 6000).then(function (recovered) {
    _recoveryInFlight = false;
    try { setLoadingRecoveryActive(false); } catch (_) {}
    if (recovered) {
      stopLoadingTicker();
      _activeGen = null;
      track('generation_recovered_after_drop', {
        generation_id:  genId,
        original_error: 'loader_deadline',
      });
      return;
    }
    // ~5.5 minutes total (150s loader + ~210s polling) — fail honestly now.
    stopLoadingTicker();
    _activeGen = null;
    try { window.WC._genInFlight = false; window.WC._minimized = false; } catch (_) {}
    try { if (zone) revertZoneInFlight(zone); } catch (_) {}
    track('generation_failed', {
      generation_id: genId,
      phase:   'deadline_recovery',
      message: 'recovery_exhausted',
    });
    showModalError(
      'Something went wrong',
      "We couldn't finish your art this time. Your photo is saved — please try again in a minute."
    );
  });
});

// ─── Public: maybeRecoverPendingGen ──────────────────────────────────────────
// 2026-05-15 R17 — On page load, check sessionStorage for a pending-gen marker
// left by handleFile. If found and fresh, attempt to fetch the completed gen
// from the orchestrator's fetch-gen action and hydrate the result modal.
// Monolith lines ~4776-4860.

export function maybeRecoverPendingGen() {
  // TEST HOOK — ?wc_forcedrop=<generationId> (or =1 to use the last pending gen)
  // simulates the dropped-connection recovery on demand, so the recovery render
  // path can be verified without forcing a real network drop. No-op in normal
  // use (the param is never present for real traffic).
  try {
    var _fd = new URLSearchParams(location.search).get('wc_forcedrop');
    if (_fd) {
      var _gid = (_fd === '1')
        ? ((JSON.parse(sessionStorage.getItem('wc_pending_gen') || '{}') || {}).generationId || '')
        : _fd;
      log('[forcedrop] simulating recovery for ' + (_gid || '(none)'));
      if (!_gid) { showModalError('Recovery test', 'Pass ?wc_forcedrop=<generationId>'); return; }
      // Replicate the REAL drop path (the inline onError recovery): the modal is
      // ALREADY open in 'generating' on a settled page when recovery fires, so the
      // result slider lays out into an already-sized modal (the cold reload path
      // is rarer; this test mirrors the warm path that the reported bug hit).
      openModal('generating');
      if (typeof startLoadingTicker === 'function') startLoadingTicker();
      setTimeout(function () {
        _recoverGenById(_gid, 9, 6000).then(function (ok) {
          log('[forcedrop] recovered=' + ok);
          if (typeof stopLoadingTicker === 'function') stopLoadingTicker();
          if (!ok) showModalError('Recovery test failed', 'No completed gen found for ' + _gid);
        });
      }, 2500);
      return;
    }
  } catch (e) { log('[forcedrop] error ' + (e && e.message)); }

  var raw;
  try { raw = sessionStorage.getItem('wc_pending_gen'); } catch (e) { return; }
  if (!raw) return;

  var pending;
  try { pending = JSON.parse(raw); } catch (e) { return; }
  if (!pending || !pending.generationId) {
    try { sessionStorage.removeItem('wc_pending_gen'); } catch (e) {}
    return;
  }

  var ageMs    = Date.now() - (pending.startedAt || 0);
  var STALE_MS = 15 * 60 * 1000; // 15 min — long enough for any successful gen
  if (ageMs > STALE_MS) {
    log('[pending-gen-recovery] marker is stale (' + Math.round(ageMs / 1000) + 's old) — clearing');
    try { sessionStorage.removeItem('wc_pending_gen'); } catch (e) {}
    return;
  }

  // If we already have a R.generationId (e.g. tryHydrateChildPdpFromUrl
  // already ran for this URL), don't double-hydrate.
  var wc = _wc();
  if (wc._currentResult && wc._currentResult.generationId === pending.generationId) {
    log('[pending-gen-recovery] already hydrated for this generationId — clearing marker');
    try { sessionStorage.removeItem('wc_pending_gen'); } catch (e) {}
    return;
  }

  log('[pending-gen-recovery] found pending gen, attempting recovery', {
    generationId:  pending.generationId,
    ageSec:        Math.round(ageMs / 1000),
    productHandle: pending.productHandle,
  });

  // Reuse the shared, corrected, unit-tested recovery (sends generationIdPrefix
  // + maps the real fetch-gen shape). On reload the gen has usually finished, so
  // a few quick attempts suffice. _recoverGenById clears the marker on success.
  _recoverGenById(pending.generationId, 3, 2000).then(function (ok) {
    if (ok) {
      log('[pending-gen-recovery] gen recovered — result modal opened');
    } else {
      // 2026-06-10 — KEEP the marker. A reload mid-generation lands here while
      // the gen is still running server-side; clearing meant the art became
      // unrecoverable the moment it finished. The marker self-expires via the
      // STALE_MS gate above, so a genuinely dead gen still ages out.
      log('[pending-gen-recovery] fetch-gen not ready yet — keeping marker for a later retry');
    }
  });
}

// ─── Flow-module plumbing exports (2026-07-01, Flow B build) ─────────────────
// The new flow modules (wc77-flow-b.js, later wc77-flow-c.js) REUSE this
// module's validation + presign→PUT→orchestrator plumbing instead of forking
// it (flow-ui-spec §Common 4 "Reuse, do not fork"). Purely additive: nothing
// here changes behavior for the modal flow — these are the exact functions
// handleFile already drives. (buildOrchestratorPayload and pollFetchGen are
// exported inline at their definitions above.)
export {
  isAcceptedFile,
  requestPresignedUrl,
  uploadToS3,
  _runFullGeneration as runFullGeneration,
};
